import { DefaultVariants } from "@outschool/experiments-shared";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import { addParamsToUrl, pathnameWithoutLocale } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import React from "react";
import { useLocation } from "react-router-dom";

import useExperiment from "../hooks/useExperiment";

export const CLP_FILTER_PERSISTENCE_EXPERIMENT = "ClpFilterPersistence";

const PERSISTABLE_FILTER_KEYS = new Set([
  "age",
  "gradeLevel",
  "theme",
  "fullCurriculum",
  "classTypes",
  "dow",
  "startAfterTime",
  "endByTime",
  "startAfter",
  "startBefore",
  "endBy",
  "pricePerMeetingMin",
  "pricePerMeetingMax",
  "pricePerCourseMin",
  "pricePerCourseMax",
  "priceCreditsPerMeetingMin",
  "priceCreditsPerMeetingMax",
  "priceCreditsPerCourseMin",
  "priceCreditsPerCourseMax",
  "fundingPrograms",
  "curriculums",
  "standards",
  "learningNeeds",
  "hasHomework",
  "includedWithMembership",
]);

function parseDestinationQuery(path: string): Record<string, string> {
  try {
    const url = new URL(path, "https://outschool.com");
    return Object.fromEntries(url.searchParams.entries());
  } catch (_error) {
    return {};
  }
}

export function getPersistableClpFilters(
  query: Record<string, unknown> | null | undefined,
): Record<string, string | number | boolean> {
  if (!query) {
    return {};
  }
  return Object.entries(query).reduce((acc, [key, value]) => {
    if (!PERSISTABLE_FILTER_KEYS.has(key)) {
      return acc;
    }
    if (value === undefined || value === null || value === "") {
      return acc;
    }
    acc[key] = Array.isArray(value)
      ? value.join(",")
      : (value as string | number | boolean);
    return acc;
  }, {} as Record<string, string | number | boolean>);
}

export function mergePersistedFiltersForDestination({
  destinationPath,
  persistedFilters,
}: {
  destinationPath: string;
  persistedFilters: Record<string, string | number | boolean>;
}) {
  const destinationQuery = parseDestinationQuery(destinationPath);
  const mergedFilters = {
    ...persistedFilters,
    ...destinationQuery,
  };

  const [basePath] = destinationPath.split("?");
  return addParamsToUrl(basePath, mergedFilters);
}

export function isClpPath(pathname: string): boolean {
  const pathWithoutLocale = pathnameWithoutLocale(pathname);
  return (
    pathWithoutLocale === "/online-classes" ||
    pathWithoutLocale.startsWith("/online-classes/") ||
    pathWithoutLocale === "/landing" ||
    pathWithoutLocale.startsWith("/landing/")
  );
}

export function isClpFilterPersistenceExperimentEligible({
  pathname,
  isLoggedIn,
}: {
  pathname: string;
  isLoggedIn: boolean;
}): boolean {
  return !isLoggedIn && isClpPath(pathname);
}

export function shouldPersistClpFilters({
  pathname,
  isLoggedIn,
  variant,
}: {
  pathname: string;
  isLoggedIn: boolean;
  variant: DefaultVariants;
}): boolean {
  return (
    isClpFilterPersistenceExperimentEligible({ pathname, isLoggedIn }) &&
    variant === "treatment"
  );
}

export function useClpFilterPersistence() {
  const location = useLocation();
  const { isLoggedIn } = useSession();
  const { variant, trigger } = useExperiment(CLP_FILTER_PERSISTENCE_EXPERIMENT);
  const isEligible = isClpFilterPersistenceExperimentEligible({
    pathname: location.pathname,
    isLoggedIn,
  });
  const shouldPersist = React.useMemo(
    () =>
      shouldPersistClpFilters({
        pathname: location.pathname,
        isLoggedIn,
        variant,
      }),
    [isLoggedIn, location.pathname, variant],
  );
  const persistedFilters = React.useMemo(
    () =>
      isEligible
        ? getPersistableClpFilters(
            Object.fromEntries(new URLSearchParams(location.search).entries()),
          )
        : {},
    [isEligible, location.search],
  );
  const getPathWithPersistedFilters = React.useCallback(
    (destinationPath: string) =>
      shouldPersist
        ? mergePersistedFiltersForDestination({
            destinationPath,
            persistedFilters,
          })
        : destinationPath,
    [persistedFilters, shouldPersist],
  );
  const triggerPersistenceExperiment = React.useCallback(() => {
    if (isEligible) {
      trigger();
    }
  }, [isEligible, trigger]);

  return {
    getPathWithPersistedFilters,
    persistedFilters,
    shouldPersist,
    triggerPersistenceExperiment,
  };
}

export function mergeClpFiltersWithSearchFilters({
  persistedFilters,
  nextFilters,
}: {
  persistedFilters: Record<string, string | number | boolean>;
  nextFilters: Partial<SearchFiltersType>;
}): SearchFiltersType {
  return {
    ...persistedFilters,
    ...nextFilters,
  };
}
